import app from 'flarum/forum/app';
import { override } from 'flarum/common/extend';
import HomeState from 'ext:ramon/avocado/forum/states/HomeState';

const parseTagIds = (raw) => {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(String(raw));
    return (Array.isArray(parsed) ? parsed : [parsed]).map(String).filter(Boolean);
  } catch (_) {
    const value = String(raw).trim();
    return value ? [value] : [];
  }
};

const activityTime = (discussion) => {
  const value = discussion.lastPostedAt?.() || discussion.createdAt?.();
  const time = value ? new Date(value).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
};

app.initializers.add('discovolante-amigacafe-avocado-home-tweaks', () => {
  // Avocado's HomePage calls HomeState.popularDiscussions(5).
  // Keep the same rendering, but make that data source mean "latest activity".
  // Showcase discussions stay excluded, matching Avocado's existing behavior.
  override(HomeState.prototype, 'popularDiscussions', function (_original, limit = 5) {
    return [...this.allDiscussions()]
      .filter((discussion) => !this.isShowcaseDiscussion(discussion))
      .sort((a, b) => activityTime(b) - activityTime(a))
      .slice(0, limit);
  });

  // Avocado normally asks HomeState for top-level categories only. If Featured
  // Categories contains child tags, put those explicitly selected tags first,
  // then fill any remaining slots with Avocado's normal top-level categories.
  // Selecting 7 featured tags therefore gives you exactly those 7 homepage cards.
  override(HomeState.prototype, 'topCategories', function (original, limit = 7) {
    const selectedIds = parseTagIds(app.forum?.attribute('avocadoFeaturedTags'));
    if (!selectedIds.length) return original(limit);

    const allTags = app.store.all('tags').filter((tag) => tag && tag.id?.());
    const byId = new Map(allTags.map((tag) => [String(tag.id()), tag]));
    const selected = selectedIds.map((id) => byId.get(id)).filter(Boolean);
    const selectedSet = new Set(selected.map((tag) => String(tag.id())));

    const fallback = original(limit).filter((tag) => !selectedSet.has(String(tag.id?.())));
    return [...selected, ...fallback].slice(0, limit);
  });

  // If no custom Avocado section title has been configured, change only the
  // client-side fallback label. An explicit admin value always wins.
  const currentHeading = String(app.forum?.attribute('avocadoPopularHeading') || '').trim();
  if (!currentHeading && app.forum?.pushAttributes) {
    app.forum.pushAttributes({ avocadoPopularHeading: 'Recent discussions' });
  }
});


/* AMIGACAFE_SECONDARY_TAG_FIRST v6
 *
 * IMPORTANT: Avocado's homepage composer does NOT use Flarum's
 * TagSelectionModal. It uses Avocado's own shared/TagPicker.
 *
 * Avocado 2.1.10 disables every non-root tag while primaryCount === 0.
 * InlineComposerState.addTag() already auto-adds the clicked tag's parent.
 * Therefore we only need to allow a child through canSelectTag() when its
 * parent can be auto-added.
 */
import { override as amigaCafeOverride } from 'flarum/common/extend';

app.initializers.add('discovolante-amigacafe-secondary-tag-first-v6', () => {
  window.__amigaCafeSecondaryTagFirst = 'v6';

  const avocadoTagPicker =
    'ext:ramon/avocado/forum/components/shared/TagPicker';

  amigaCafeOverride(
    avocadoTagPicker,
    'canSelectTag',
    function (original, tag, state, limits, primaryCount, secondaryCount) {
      // Preserve stock Avocado behaviour whenever it already allows the tag.
      const stock = original(tag, state, limits, primaryCount, secondaryCount);
      if (stock) return true;

      if (!tag || !state || !limits) return false;

      const isChild = !!tag.isChild?.();

      // This is the exact case we want to change:
      // Avocado rejected a child only because no root primary is selected yet.
      if (isChild && primaryCount === 0 && !state.tags.includes(tag)) {
        const parent = tag.parent?.();

        if (!parent || parent === false) return false;

        // The click will auto-add the parent, so there must be room for it.
        if (limits.maxPrimary <= 0 || primaryCount >= limits.maxPrimary) {
          return false;
        }

        // Preserve Avocado's existing non-primary max-secondary guard.
        if (secondaryCount >= limits.maxSecondary) {
          return false;
        }

        // Respect the tag permissions exposed by Flarum when available.
        if (typeof tag.canStartDiscussion === 'function' &&
            tag.canStartDiscussion() === false) {
          return false;
        }

        if (typeof parent.canStartDiscussion === 'function' &&
            parent.canStartDiscussion() === false) {
          return false;
        }

        return true;
      }

      return false;
    }
  );
});
